/**
 * Conversations entre l'étudiant et la scolarité.
 * Liste des fils + chat ouvert. Charte sobre cohérente avec le portail.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Loader2, MessageSquare, Check, CheckCheck, Inbox, Trash2, Smile,
  Image as ImageIcon, Paperclip, X,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import {
  portailApi, ThreadMessageScolarite, PostMessageItem,
} from '../services/portailApi';

const EMOJIS_MSG = [
  '🙂', '😊', '😀', '😃', '😄', '😁',
  '🙏', '👍', '👌', '💪', '🤝', '🙌',
  '❤️', '🧡', '💛', '💚', '💙', '💜',
  '🎉', '✨', '🔥', '⭐', '💯', '☀️',
];

function MsgEmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  return (
    <div className="absolute bottom-full mb-2 left-0 z-30 bg-gray-800 border border-gray-700 rounded-xl shadow-xl p-2 w-64">
      <div className="grid grid-cols-6 gap-1">
        {EMOJIS_MSG.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => { onPick(e); onClose(); }}
            className="text-xl p-1.5 rounded hover:bg-gray-700 active:bg-gray-600"
          >
            <span className="emoji-hover-bounce">{e}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

interface MesMessagesScolariteProps {
  /** Si true, n'enveloppe pas dans PortailLayout (utilisé par Messagerie.tsx). */
  embedded?: boolean;
}

export default function MesMessagesScolarite({ embedded = false }: MesMessagesScolariteProps = {}) {
  const navigate = useNavigate();
  const { id: threadIdParam } = useParams<{ id?: string }>();
  const [threads, setThreads] = useState<ThreadMessageScolarite[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
  const [messages, setMessages] = useState<PostMessageItem[]>([]);
  const [threadInfo, setThreadInfo] = useState<{ sujet: string; traite?: boolean } | null>(null);
  const [loadingChat, setLoadingChat] = useState(false);

  // Composer
  const [showCompose, setShowCompose] = useState(false);
  const [sujet, setSujet] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  // Pièce jointe pour envoi (image/audio/fichier)
  const [fichierAttache, setFichierAttache] = useState<File | null>(null);
  const [fichierPreview, setFichierPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  useEffect(() => {
    if (!fichierAttache) { setFichierPreview(null); return; }
    if (fichierAttache.type.startsWith('image/')) {
      const url = URL.createObjectURL(fichierAttache);
      setFichierPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setFichierPreview(null);
  }, [fichierAttache]);
  const [error, setError] = useState<string | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const refreshList = async () => {
    setLoadingList(true);
    try {
      const r = await portailApi.listMessagesScolarite();
      setThreads(r.results);
    } catch (e: any) {
      if (e.status === 401) navigate('/portail/login', { replace: true });
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    refreshList();
  }, []);

  useEffect(() => {
    if (threadIdParam) {
      const id = parseInt(threadIdParam, 10);
      if (!isNaN(id)) openThread(id);
    }
  }, [threadIdParam]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const openThread = async (id: number) => {
    setSelectedThreadId(id);
    setLoadingChat(true);
    setError(null);
    try {
      const r = await portailApi.getMessageScolariteThread(id);
      setThreadInfo({ sujet: r.thread.sujet, traite: !!(r.thread as any).traite });
      setMessages(r.messages);
      // Mettre à jour le compteur dans la liste
      setThreads((prev) => prev.map((t) => t.id === id ? { ...t, nb_non_lus: 0 } : t));
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoadingChat(false);
    }
  };

  const sendNewMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim().length < 10) {
      setError('Message trop court (10 caractères min)');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const r = await portailApi.envoyerMessageScolarite({
        sujet: sujet.trim() || undefined,
        message: newMessage.trim(),
      });
      setShowCompose(false);
      setSujet('');
      setNewMessage('');
      await refreshList();
      openThread(r.thread_id);
    } catch (e: any) {
      setError(e?.message || 'Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm('Supprimer ce message ? Cette action est irréversible.')) return;
    try {
      await portailApi.supprimerPostMessageScolarite(postId);
      if (selectedThreadId) await openThread(selectedThreadId);
    } catch (e: any) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedThreadId) return;
    const hasAttachment = !!fichierAttache;
    if (!hasAttachment && reply.trim().length < 2) return;
    setSending(true);
    setError(null);
    try {
      await portailApi.envoyerMessageScolarite({
        thread_id: selectedThreadId,
        message: reply.trim(),
        fichier: fichierAttache || undefined,
      });
      setReply('');
      setFichierAttache(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await openThread(selectedThreadId);
      refreshList();
    } catch (e: any) {
      if (e?.status === 409 && e?.data?.thread_clos) {
        setError(e.data.detail || 'Cette conversation a été clôturée. Ouvrez un nouveau sujet.');
        await openThread(selectedThreadId);
      } else {
        setError(e?.message || 'Erreur d\'envoi');
      }
    } finally {
      setSending(false);
    }
  };

  // Coller une image depuis le presse-papier (Ctrl+V)
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    for (const it of items) {
      if (it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          setFichierAttache(file);
          return;
        }
      }
    }
  };

  if (selectedThreadId !== null) {
    // Vue chat
    const chatContent = (
      <>
        <div className={embedded ? 'flex flex-col h-[calc(100vh-200px)]' : 'flex flex-col h-[calc(100vh-120px)]'}>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => { setSelectedThreadId(null); setMessages([]); refreshList(); }}
              className="p-2 hover:bg-gray-800 rounded text-gray-300"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-100 truncate">
                {threadInfo?.sujet || 'Conversation'}
              </div>
              <div className="text-[10px] text-gray-400">avec la scolarité</div>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto bg-gray-950 border border-gray-800 rounded-lg p-3 space-y-2"
          >
            {loadingChat ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                <Loader2 className="inline-block animate-spin mr-2" size={14} /> Chargement...
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Aucun message</div>
            ) : (
              messages.map((m) => {
                const own = m.expediteur === 'etudiant';
                const isPost = typeof m.id === 'number';
                return (
                  <div
                    key={m.id}
                    className={`flex ${own ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] flex flex-col ${own ? 'items-end' : 'items-start'}`}>
                      <div
                        className={`rounded-lg px-3 py-2 ${
                          m.supprime
                            ? 'bg-gray-800/60 border border-dashed border-gray-700 text-gray-400 italic'
                            : own
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-800 text-gray-100 border border-gray-700'
                        }`}
                      >
                        {!own && m.expediteur_nom && !m.supprime && (
                          <div className="text-[10px] text-gray-400 mb-0.5">
                            {m.expediteur_nom}
                          </div>
                        )}
                        {/* Pièce jointe selon le type */}
                        {!m.supprime && m.type === 'image' && m.fichier_url && (
                          <img src={m.fichier_url} alt=""
                            onClick={() => setLightboxImage(m.fichier_url!)}
                            className="max-w-[260px] max-h-[260px] rounded mb-1 cursor-zoom-in object-cover" />
                        )}
                        {!m.supprime && m.type === 'audio' && m.fichier_url && (
                          <audio controls src={m.fichier_url} className="max-w-[260px] mb-1" />
                        )}
                        {!m.supprime && m.type === 'fichier' && m.fichier_url && (
                          <a href={m.fichier_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2 py-1 mb-1 bg-white/15 rounded text-xs hover:underline">
                            <Paperclip size={11} /> {m.fichier_nom_original || 'Télécharger'}
                          </a>
                        )}
                        {(!m.type || m.type === 'texte' || m.contenu) && (
                          <div className="text-sm whitespace-pre-wrap break-words">
                            {m.supprime
                              ? `Message supprimé${m.supprime_par ? ` par ${m.supprime_par === 'etudiant' ? 'vous' : 'la scolarité'}` : ''}`
                              : m.contenu}
                          </div>
                        )}
                        <div className={`text-[10px] mt-1 ${own ? 'text-blue-200/80' : 'text-gray-500'}`}>
                          {fmtDate(m.date_creation)}
                        </div>
                      </div>
                      {/* Accusé de lecture pour mes propres messages + bouton supprimer */}
                      <div className={`flex items-center gap-2 mt-0.5 px-1 text-[10px] ${own ? 'flex-row-reverse' : ''}`}>
                        {own && !m.supprime && (
                          m.lu_par_destinataire ? (
                            <span className="text-blue-300 inline-flex items-center gap-0.5" title={m.date_lecture ? `Lu par la scolarité le ${new Date(m.date_lecture).toLocaleString('fr-FR')}` : 'Lu par la scolarité'}>
                              <CheckCheck size={10} /> Lu
                            </span>
                          ) : (
                            <span className="text-gray-500 inline-flex items-center gap-0.5" title="Pas encore lu">
                              <Check size={10} /> Envoyé
                            </span>
                          )
                        )}
                        {m.peut_supprimer && isPost && (
                          <button
                            onClick={() => handleDelete(m.id as number)}
                            className="text-red-400 hover:text-red-300 inline-flex items-center gap-0.5"
                            title="Supprimer ce message"
                          >
                            <Trash2 size={10} /> Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Composer réponse — désactivé si la scolarité a clôturé la conversation */}
          {threadInfo?.traite ? (
            <div className="mt-2 bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 text-sm text-amber-200 space-y-2">
              <div className="font-medium">Conversation clôturée par la scolarité.</div>
              <div className="text-xs text-amber-200/80">
                Pour reprendre contact, ouvrez un nouveau sujet depuis votre boîte de réception.
              </div>
              <button
                onClick={() => {
                  setSelectedThreadId(null);
                  setMessages([]);
                  setShowCompose(true);
                  refreshList();
                }}
                className="mt-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded inline-flex items-center gap-1.5"
              >
                <Send size={12} /> Nouveau sujet
              </button>
            </div>
          ) : (
            <form onSubmit={sendReply} className="mt-2 space-y-2">
              {/* Preview de la pièce jointe */}
              {fichierAttache && (
                <div className="inline-flex items-start gap-2 p-2 bg-gray-800 border border-gray-700 rounded-lg">
                  {fichierPreview ? (
                    <img src={fichierPreview} alt=""
                      className="w-16 h-16 object-cover rounded cursor-zoom-in"
                      onClick={() => setLightboxImage(fichierPreview)} />
                  ) : (
                    <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center text-gray-400">
                      <Paperclip size={20} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-100 truncate">{fichierAttache.name}</div>
                    <div className="text-[10px] text-gray-400">{Math.round(fichierAttache.size / 1024)} KB</div>
                    <button type="button"
                      onClick={() => { setFichierAttache(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="mt-0.5 text-[10px] text-red-400 hover:text-red-300 inline-flex items-center gap-0.5">
                      <X size={10} /> Retirer
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-end gap-2 relative">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowEmoji((v) => !v)}
                    className="p-2 text-gray-400 hover:text-amber-400 hover:bg-gray-800 rounded"
                    title="Insérer un émoji"
                  >
                    <Smile size={16} />
                  </button>
                  {showEmoji && (
                    <MsgEmojiPicker
                      onPick={(emo) => setReply((v) => v + emo)}
                      onClose={() => setShowEmoji(false)}
                    />
                  )}
                </div>
                <input ref={fileInputRef} type="file"
                  accept="image/*,audio/*,application/pdf,.doc,.docx"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setFichierAttache(f); }}
                  className="hidden" />
                <button type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-800 rounded"
                  title="Joindre une image ou un fichier">
                  <ImageIcon size={16} />
                </button>
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onPaste={handlePaste}
                  rows={2}
                  placeholder="Votre réponse… (Ctrl+V pour coller une image)"
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendReply(e as any);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={sending || (!fichierAttache && reply.trim().length < 2)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-1.5 text-sm"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </form>
          )}
          {error && (
            <div className="mt-2 text-xs text-red-400">{error}</div>
          )}
        </div>
        {lightboxImage && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxImage(null)}>
            <button onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white">
              <X size={24} />
            </button>
            <img src={lightboxImage} alt=""
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </>
    );
    return embedded
      ? chatContent
      : <PortailLayout showHeader title={threadInfo?.sujet || 'Conversation'}>{chatContent}</PortailLayout>;
  }

  // Vue liste
  const listeContent = (
    <div className="space-y-3">
        <div className="flex items-center gap-2">
          {!embedded && (
            <button
              onClick={() => navigate('/portail/accueil')}
              className="p-2 hover:bg-gray-800 rounded text-gray-300"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h2 className="font-semibold text-gray-100 text-sm flex-1">Conversations</h2>
          <button
            onClick={() => setShowCompose(true)}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded inline-flex items-center gap-1.5"
          >
            <Send size={13} /> Nouveau
          </button>
        </div>

        {showCompose && (
          <form onSubmit={sendNewMessage} className="bg-gray-900 border border-blue-700 rounded-lg p-4 space-y-3">
            <input
              type="text"
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              placeholder="Sujet (optionnel)"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500"
            />
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              rows={4}
              required
              minLength={10}
              placeholder="Votre message... (10 caractères minimum)"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500"
            />
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowCompose(false); setError(null); }}
                className="px-3 py-1.5 text-xs text-gray-300 hover:text-gray-100"
              >Annuler</button>
              <button
                type="submit"
                disabled={sending}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded inline-flex items-center gap-1.5"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Envoyer
              </button>
            </div>
          </form>
        )}

        {loadingList ? (
          <div className="text-center py-12">
            <Loader2 className="inline-block animate-spin w-6 h-6 text-blue-500" />
          </div>
        ) : threads.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <Inbox className="w-10 h-10 mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-300">Aucune conversation</p>
            <button
              onClick={() => setShowCompose(true)}
              className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded inline-flex items-center gap-1.5"
            >
              <Send size={13} /> Démarrer une conversation
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => openThread(t.id)}
                className={`w-full text-left bg-gray-900 border rounded-lg p-3 transition-colors ${
                  t.nb_non_lus > 0
                    ? 'border-blue-700 hover:border-blue-600'
                    : 'border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <MessageSquare size={13} className="text-blue-400 flex-shrink-0" />
                    <span className="font-semibold text-sm text-gray-100 truncate">
                      {t.sujet || '(Sans sujet)'}
                    </span>
                    {t.traite && t.nb_non_lus === 0 && (
                      <Check size={12} className="text-green-400 flex-shrink-0" />
                    )}
                  </div>
                  {t.nb_non_lus > 0 && (
                    <span className="text-[10px] font-bold bg-blue-600 text-white rounded-full px-2 py-0.5">
                      {t.nb_non_lus}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mb-1 line-clamp-1">
                  <span className="text-gray-500">
                    {t.dernier_expediteur === 'etudiant' ? 'Vous : ' : 'Scolarité : '}
                  </span>
                  {t.dernier_apercu}
                </div>
                <div className="text-[10px] text-gray-500">
                  {fmtDate(t.derniere_activite)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
  );
  return embedded
    ? listeContent
    : <PortailLayout showHeader title="Mes messages scolarité">{listeContent}</PortailLayout>;
}
